import { supabase } from '../utils/supabase';
import rateLimiter from '../utils/rateLimiter';

export const SITE_FEEDBACK_CATEGORIES = [
  { value: 'bug', label: 'Bug or problem' },
  { value: 'idea', label: 'Feature idea' },
  { value: 'general', label: 'General feedback' },
];

const MESSAGE_MAX = 2000;
const RATE = { maxRequests: 5, windowMs: 60 * 60 * 1000 };

function rateKeyForAuthUserId(authUserId) {
  return `site-feedback-${authUserId}`;
}

/**
 * @param {string} category
 * @param {string} message
 * @returns {{ error?: string }}
 */
export function validateSiteFeedbackInput({ category, message }) {
  const allowed = SITE_FEEDBACK_CATEGORIES.map((c) => c.value);
  if (!category || !allowed.includes(category)) {
    return { error: 'Please choose a category.' };
  }
  const trimmed = (message || '').trim();
  if (trimmed.length < 1) {
    return { error: 'Please enter a message.' };
  }
  if (trimmed.length > MESSAGE_MAX) {
    return { error: `Message must be at most ${MESSAGE_MAX} characters.` };
  }
  return {};
}

/**
 * @param {Object} args
 * @param {string} args.category
 * @param {string} args.message
 * @param {string} [args.pagePath] - raw pathname (for support / debugging)
 * @param {string} [args.pageLabel] - human-readable page name (see getFeedbackPageLabel)
 * @param {string} [args.userAgent]
 */
export async function submitSiteFeedback({ category, message, pagePath, pageLabel, userAgent }) {
  // getUser() validates the JWT; refreshSession() ensures the REST client has a fresh access token
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  if (userError || !user?.id) {
    throw new Error('You must be signed in to send feedback.');
  }
  const { data: { session: existing } } = await supabase.auth.getSession();
  if (!existing?.access_token) {
    const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError || !refreshed?.session?.access_token) {
      throw new Error('Your session has expired. Please sign in again and try once more.');
    }
  } else {
    const soon =
      existing.expires_at != null && existing.expires_at * 1000 < Date.now() + 60_000;
    if (soon) {
      const { data: refreshed } = await supabase.auth.refreshSession();
      if (refreshed?.session) {
        /* token attached to client for the following insert */
      }
    }
  }

  const { data: playerRows, error: playerError } = await supabase
    .from('players')
    .select('id')
    .eq('auth_id', user.id)
    .limit(1);
  if (playerError) {
    throw new Error('Could not load your profile. Please try again.');
  }
  const resolvedId = playerRows?.[0]?.id;
  if (!resolvedId) {
    throw new Error('No player profile is linked to this account. Please contact support.');
  }

  const validation = validateSiteFeedbackInput({ category, message });
  if (validation.error) {
    throw new Error(validation.error);
  }

  const key = rateKeyForAuthUserId(user.id);
  if (!rateLimiter.isAllowed(key, RATE)) {
    const ms = rateLimiter.getTimeUntilReset(key, RATE);
    const mins = Math.max(1, Math.ceil(ms / 60000));
    throw new Error(`Too many submissions. Please try again in about ${mins} minute${mins === 1 ? '' : 's'}.`);
  }

  const trimmed = message.trim();
  const { error } = await supabase
    .from('site_feedback')
    .insert({
      player_id: resolvedId,
      category,
      message: trimmed,
      page_path: pagePath || null,
      page_label: pageLabel || null,
      user_agent: userAgent || null,
      status: 'new',
    });

  if (error) {
    const code = error?.code != null ? String(error.code) : '';
    const rls = /row-level security|rls|42501/i.test(`${error.message || ''} ${code}`) || code === '42501';
    if (rls) {
      throw new Error(
        'Feedback could not be saved (server security check). ' +
          'If this keeps happening, re-apply the site_feedback RLS in migration 060 on this Supabase project, or try signing out and back in.'
      );
    }
    throw new Error(error.message || 'Failed to send feedback. Please try again.');
  }

  return {};
}
